// The person's own day, in their time zone (Settings → Your day, the zone the
// plan, the brief and the server's "today" already use), not the browser's.
// A browser in another zone, or a day out, would otherwise show one day's
// heading and ribbon beside another day's plan.

const formatters = new Map<string, Intl.DateTimeFormat>();

/** The zone if the browser knows it, else undefined (the browser's own). */
export function knownZone(tz?: string | null): string | undefined {
  if (!tz) return undefined;
  try {
    // Throws a RangeError for a zone the browser doesn't know.
    new Intl.DateTimeFormat('en-GB', { timeZone: tz });
    return tz;
  } catch {
    return undefined;
  }
}

function partsFormatter(tz: string | undefined): Intl.DateTimeFormat {
  const key = tz ?? '';
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    formatters.set(key, f);
  }
  return f;
}

interface WallClock { date: string; minutes: number }

/** The calendar date (yyyy-MM-dd) and minute of the day an instant falls on in a zone. */
export function wallClock(instant: Date, tz?: string | null): WallClock {
  const parts = partsFormatter(knownZone(tz)).formatToParts(instant);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00';
  // Some engines say "24" for midnight even with h23.
  const hour = Number(get('hour')) % 24;
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    minutes: hour * 60 + Number(get('minute')),
  };
}

/** The person's date today (yyyy-MM-dd) in their zone. */
export function dateInZone(instant: Date, tz?: string | null): string {
  return wallClock(instant, tz).date;
}

/** Minutes since midnight of an instant, in the zone. */
export function minutesInZone(instant: Date, tz?: string | null): number {
  return wallClock(instant, tz).minutes;
}

/** "09:30" for a minute of the day. */
export function clockTime(minutes: number): string {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** Minutes since midnight of an "HH:mm" or "HH:mm:ss" time. */
export function minutesOfTime(hhmm: string | null | undefined): number | null {
  const match = /^(\d{1,2}):(\d{2})/.exec(hhmm ?? '');
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

/** "Saturday 31 October" for a yyyy-MM-dd date, whatever zone the browser is in. */
export function longDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString('en-GB', {
    timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long',
  });
}

/** yyyy-MM-dd plus or minus some days. */
export function addDaysTo(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + days, 12));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
}

/** "Good morning" by the hour where the person is. */
export function greetingAt(minutes: number): string {
  if (minutes < 12 * 60) return 'Good morning';
  if (minutes < 17 * 60) return 'Good afternoon';
  return 'Good evening';
}
